import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import {
    addRetailerTypeSelection,
    getSelectedRetailerTypes,
    RetailerTypeState,
    getRetailerTypes,
    getRetailerTypeState,
    clearFilters,
    updateCheckboxes,
    getCheckboxState,
    getNextPageRetailerTypes,
    searchRetailerTypes,
    setCheckboxes,
    getCountries,
} from '@emi/shared/state/filters';
import { Paging } from '@emi/shared/constants';
import { RetailerType, CheckboxTreeNode, Page } from '@emi/shared/models';
import { select, Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import * as Constants from '@emi/shared/constants';

@Component({
    selector: 'via-filter-retailer-types',
    templateUrl: './filter-retailer-types.component.html',
    styleUrls: ['./filter-retailer-types.component.scss'],
})
export class FilterRetailerTypesComponent implements OnInit, OnDestroy {
    @Input()
    public applyClicked: EventEmitter<boolean>;

    @Input()
    public cancelClicked: EventEmitter<boolean>;

    @Input()
    public showApplyButton: boolean;

    @Input()
    public isOverlay: boolean;

    @Output()
    public showApplyButtonChange = new EventEmitter<boolean>();

    @ViewChild('scrollArea')
    public scrollArea: ElementRef;

    @ViewChildren('page')
    public pageElements: QueryList<ElementRef>;

    @ViewChildren('pageAnchor')
    public pageAnchorElements: QueryList<ElementRef>;

    private retailerTypesSubscription: Subscription;
    private applyClickedSubscription: Subscription;
    private cancelClickedSubscription: Subscription;
    private checkBoxSubscription: Subscription;
    private stateSubscription: Subscription;
    public retailerTypesTree: Array<CheckboxTreeNode> = [];
    public selectedRetailerTypes: Array<RetailerType> = [];
    public stateRetailerTypes: Array<RetailerType> = [];
    public checkedArray: Array<CheckboxTreeNode> = [];
    public loading: boolean;
    public showError = false;
    public errorMessage = '';
    public pages: Page[] = [];
    public totalSize: number | undefined;
    public eventsSubject: Subject<CheckboxTreeNode[]> = new Subject<CheckboxTreeNode[]>();
    private nextPageId: string | undefined;
    public hasSearchedByText = false;
    public noResultsMessage = '';
    private isLargeViewport: boolean;

    constructor(private readonly store: Store<RetailerTypeState>, private changeDetector : ChangeDetectorRef) {}

    public onCheckboxStateChange(selectedCheckbox: CheckboxTreeNode): void {
        this.store.dispatch(updateCheckboxes({ checkbox: selectedCheckbox }));
    }

    public onScrollDown(): void {
        if (this.nextPageId) {
            this.store.dispatch(getNextPageRetailerTypes());
        }
    }

    public ngOnInit(): void {
        this.checkBoxSubscription = this.store.pipe(select(getCheckboxState)).subscribe((y) => {
            this.checkedArray = y.checkboxes;
        });

        this.retailerTypesSubscription = this.store.pipe(select(getSelectedRetailerTypes)).subscribe((retailerTypes) => {
            if (retailerTypes.length > 0) {
                if (this.checkedArray.length === 0) {
                    this.store.dispatch(
                        setCheckboxes({
                            checkboxes: retailerTypes.map((retailerType) => {
                                return this.buildCheckboxTreeNode(retailerType, true);
                            }),
                        })
                    );
                }
                this.selectedRetailerTypes = retailerTypes;
                this.retailerTypesTree = retailerTypes.map((retailerType) => {
                    return this.buildCheckboxTreeNode(retailerType);
                });
                const nextPage = new Page('', this.retailerTypesTree);
                this.pages.push(nextPage);
                this.eventsSubject.next(nextPage.value);
            }
            this.checkForResults();
        });

        this.stateSubscription = this.store.pipe(select(getRetailerTypeState)).subscribe((state) => {
            this.loading = state.loading;
            this.showError = state.failed;
            this.errorMessage = state.error;
            this.nextPageId = state.metadata?.nextPageId;
            this.totalSize = state.metadata?.totalResults;
            this.pages = [];
            if (this.selectedRetailerTypes.length === 0) {
                this.retailerTypesTree = state.retailerTypes.map((retailerType) => {
                    return this.buildCheckboxTreeNode(retailerType);
                });
            }
            this.eventsSubject.next(this.retailerTypesTree);
            while (this.retailerTypesTree.length > 0) {
                const newPage = this.retailerTypesTree.splice(0, Paging.PAGE_SIZE);
                this.pages.push(new Page(this.nextPageId, newPage));
            }
            this.stateRetailerTypes = state.retailerTypes;
            this.checkForResults();

            if (!this.isLargeViewport || this.pages.length > Constants.Paging.LARGE_VIEWPORT_PAGE_THRESHOLD) {
                this.scrollToLastPage();
            }
        });

        this.applyClickedSubscription = this.applyClicked.subscribe(() => {
            const retailerTypes: Array<RetailerType> = this.checkedArray
                .map((x) => new RetailerType(x.id, x.label))
                .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
            this.store.dispatch(addRetailerTypeSelection({ retailerTypes }));
            this.store.dispatch(getCountries());
            this.store.dispatch(clearFilters());
        });

        if (this.isOverlay) {
            this.cancelClickedSubscription = this.cancelClicked.subscribe(() => {
                this.pages = [];
                this.store.dispatch(
                    setCheckboxes({
                        checkboxes: this.selectedRetailerTypes.map((retailerType) => {
                            return this.buildCheckboxTreeNode(retailerType, true);
                        }),
                    })
                );
                const currentSelectableRetailerTypes =
                    this.selectedRetailerTypes.length > 0 ? this.selectedRetailerTypes : this.stateRetailerTypes;
                this.retailerTypesTree = currentSelectableRetailerTypes.map((retailerType) => {
                    return this.buildCheckboxTreeNode(retailerType, this.selectedRetailerTypes.length > 0);
                });
                this.eventsSubject.next(this.retailerTypesTree);
                while (this.retailerTypesTree.length > 0) {
                    const newPage = this.retailerTypesTree.splice(0, Paging.PAGE_SIZE);
                    this.pages.push(new Page(this.nextPageId, newPage));
                }
            });
        }

        if (this.selectedRetailerTypes.length === 0 && this.stateRetailerTypes.length === 0) {
            this.store.dispatch(getRetailerTypes());
        }
    }

    public buildCheckboxTreeNode(retailerType: RetailerType, completed?: boolean): CheckboxTreeNode {
        if (completed) {
            return new CheckboxTreeNode(retailerType.id, retailerType.name, 'checked');
        }
        const existingRetailerType = this.checkedArray.find((x) => x.id === retailerType.id);
        return new CheckboxTreeNode(retailerType.id, retailerType.name, existingRetailerType ? 'checked' : 'unchecked');
    }

    public selectionChanged(): void {
        this.showApplyButtonChange.emit(this.selectedRetailerTypes.length !== this.checkedArray.length && this.checkedArray.length > 0);
    }

    public searchFiltersByText(text: string): void {
        this.hasSearchedByText = true;
        this.store.dispatch(searchRetailerTypes({ searchText: text, delay: Constants.GlobalVariables.FILTER_SEARCH_DELAY }));
    }

    public resetSearch(): void {
        this.hasSearchedByText = false;
        this.store.dispatch(searchRetailerTypes({ searchText: '', delay: 0 }));
    }

    public checkForResults(): void {
        if (!this.loading && this.stateRetailerTypes.length == 0 && !this.hasSearchedByText && this.selectedRetailerTypes.length == 0) {
            this.noResultsMessage = Constants.GlobalVariables.ZERO_SKUS;
            return;
        }
        if (!this.loading && this.stateRetailerTypes.length == 0 && this.hasSearchedByText && this.selectedRetailerTypes.length == 0) {
            this.noResultsMessage = Constants.GlobalVariables.NO_SEARCH_RESULTS;
            return;
        }
        this.noResultsMessage = '';
    }

    public scrollToLastPage(): void {
        if (this.pageAnchorElements && this.pageAnchorElements.last && this.pageAnchorElements.last.nativeElement) {
            // TODO: investigate alternative to setTimeout
            setTimeout(() => {
                if (!this.isLargeViewport) {
                    this.pageAnchorElements.last.nativeElement.scrollIntoView();
                } else {
                    const pageAnchorIndex = this.pageAnchorElements.length - Constants.Paging.LARGE_VIEWPORT_PAGE_THRESHOLD;
                    this.pageAnchorElements.get(pageAnchorIndex)?.nativeElement.scrollIntoView();
                }
            }, 0);
        }
    }

    public ngOnDestroy(): void {
        if (this.hasSearchedByText && this.selectedRetailerTypes.length === 0) {
            this.store.dispatch(searchRetailerTypes({ searchText: '', delay: 0 }));
        }
        this.checkBoxSubscription.unsubscribe();
        this.stateSubscription.unsubscribe();
        this.retailerTypesSubscription.unsubscribe();
        this.applyClickedSubscription.unsubscribe();
        if (this.isOverlay) {
            this.cancelClickedSubscription.unsubscribe();
        }
        this.showApplyButtonChange.emit(false);
    }

    public checkScrollArea(): void {
        this.changeDetector.detectChanges();

        const pageHeight = this.pageElements.first.nativeElement.clientHeight;
        const scrollAreaHeight = this.scrollArea.nativeElement.clientHeight;
        const scrollDistance = this.scrollArea.nativeElement.scrollHeight;

        if (pageHeight <= scrollAreaHeight && this.totalSize && this.totalSize > Paging.PAGE_SIZE) {
            this.isLargeViewport = true;

            if (scrollDistance <= scrollAreaHeight) { // Load another page only if there is no scrollbar
                this.onScrollDown();
            }
        }
    }
}
